import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import PubNub from "pubnub";
import { PubNubProvider } from "pubnub-react";

import CommunityCategories from "../components/communityPage/CommunityCategories";
import CategoryGroups from "../components/communityPage/CategoryGroups";
import MyGroupChat from '../components/communityPage/MyGroupChat'
import { getUserGroups, getAllGroups } from "../services/groupchatService";
import SecondaryLoader from "../components/SecondaryLoader/SecondaryLoader"; 

const CommunityPage = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchParams] = useSearchParams();
  const userId = useSelector((state) => state.auth.id);

  useEffect(() => {
    if (searchParams.get('group') === 'career-transition') {
      setSelectedCategory('learning');
    }
  }, [searchParams]);

  const {
    data: allCategoriesData = { learningGroups: [], industryGroups: [] },
    isLoading: isLoadingCategories,
  } = useQuery({
    queryKey: ["allCategories"],
    queryFn: getAllGroups,
  });

  const {
    data: myGroups = [],
    isLoading: isLoadingMyGroups,
  } = useQuery({
    queryKey: ["myGroups", userId],
    queryFn: () => getUserGroups(userId),
    enabled: !!userId,
  });

  const { learningGroups, industryGroups } = useMemo(() => {
    const learningGroupsRaw = allCategoriesData.learningGroups || [];
    const industryGroupsRaw = allCategoriesData.industryGroups || [];
    
    const careerGroup = learningGroupsRaw.find(g => g.name === "Career Transition Support");
    const learningGroups = careerGroup
      ? [careerGroup, ...learningGroupsRaw.filter(g => g.name !== "Career Transition Support")]
      : learningGroupsRaw;

    return { learningGroups, industryGroups: industryGroupsRaw };
  }, [allCategoriesData]);

  const pubnub = useMemo(() => new PubNub({
    publishKey: process.env.REACT_APP_PUBNUB_PUBLISH_KEY,
    subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY,
    uuid: "user-" + userId,
  }), [userId]);

  const isAnyLoading = isLoadingCategories || isLoadingMyGroups;

  return (
    <div className="flex flex-col gap-6 h-full overflow-y-auto px-4 bg-white">
      {isAnyLoading ? (
        <div className="flex items-center justify-center flex-grow">
          <SecondaryLoader />
        </div>
      ) : !selectedCategory ? (
        <CommunityCategories 
          onCategorySelect={setSelectedCategory}
          industryGroups={industryGroups}
          learningGroups={learningGroups}
          myGroups={myGroups}
        />
      ) : selectedCategory === "my-groups" ? (
        <PubNubProvider client={pubnub}>
          <MyGroupChat chats={myGroups} onBack={() => setSelectedCategory(null)} />
        </PubNubProvider>
      ) : (
        <CategoryGroups 
          category={selectedCategory} 
          onBack={() => setSelectedCategory(null)}
          groups={selectedCategory === "learning" ? learningGroups : industryGroups}
          highlightGroup={searchParams.get('group') === 'career-transition' ? 'Career Transition Support' : null}
        />
      )}
    </div>
  );
};

export default CommunityPage;
